import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useGetDatasetDataMutation } from "@/api/dataset/useDatasetAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { ChevLeftIcon, CloseIcon } from "@/assets/icons";
import AdminAreaSelection from "@/components/adminAreaSelection";
import Divider from "@/components/ui/Divider";
import LoadingWrapper from "@/components/ui/LoadingWrapper";
import { MAX_POI } from "@/constants/dataset";
import {
  getAdminAreaRequired,
  getAdminAreaVisibility
} from "@/helpers/datasetAttributeHelper";
import { useAttributeFilter } from "@/hooks/attributeFilter/useAttributeFilter";
import { useH3Configuration } from "@/hooks/h3Configuration/useH3Configuration";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useDataset } from "@/hooks/map/useDataset";
import useLoadingAndToast from "@/hooks/ui/useLoadingAndToast";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { useDatasetStream } from "@/hooks/useDatasetStream";
import { cn } from "@/lib/utils";
import { RootState } from "@/store";
import { selectDatasetFilters } from "@/store/dataset/selectors";
import {
  toggleUseGlobalFilter,
  updateDatasetCategories,
  updateFilters
} from "@/store/slice/datasetSlice";
import { setLegendFilters } from "@/store/slice/mapSlice";
import { setDatasetConfigTemp } from "@/store/slice/spatialSlice";
import {
  Dataset,
  DatasetDetailResponse,
  DatasetFilter
} from "@/types/dataset.ts";
import { EOutputAnalysisType } from "@/types/spatial";
import {
  convertDatasetResponseToFeatureCollection,
  getDefaultSelectedCategory
} from "@/utils/dataset";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

import AggregationFilter from "./AggregationFilter";
import { AttributeFilter } from "./AttributeFilter";
import CustomFilterArea from "./CustomFilterArea";
import EmptyState from "./EmptyState";
import SavedAdmArea from "./SavedAdmArea";
import SavedFiltersList from "./SavedFiltersList";

const TEST_ID_PREFIX = "attribute-filter-popup-content";

type Props = {
  disabled?: boolean;
  onClose: () => void;
  dataset: Dataset;
  selectedDataset: DatasetDetailResponse | null;
};

const CatchmentErrorMessage = ({ datasetLength = 0 }) => (
  <span className="mb-2 text-xs text-cinnabar">
    {datasetLength === 0
      ? "No data selected. Please select at least one record to proceed."
      : `Selection exceeds the limit (${datasetLength}/${MAX_POI}). Please reduce the number of records to proceed.`}
  </span>
);

const EditPopupAttribute = ({
  disabled = false,
  onClose,
  dataset,
  selectedDataset
}: Props) => {
  const {
    isAdminTreeEnabled,
    isGlobalFilterOverrideEnabled,
    isBVTDataForCatchmentEnabled
  } = useAppFeatures();
  const { removeAllCatchmentMarkers, handleMovePositionToLayer } = useMap();
  const dispatch = useDispatch();
  const { showAnalysisResultPopup } = useSelector(
    (state: RootState) => state.ui
  );
  const { latestAnalysisId } = useSelector((state: RootState) => state.spatial);
  const datasetId = dataset.data.id;
  const datasetFiltersDatex: DatasetFilter[] = useSelector((state: RootState) =>
    selectDatasetFilters(datasetId)(state)
  );
  const { catchmentSettingsTemp } = useSelector(
    (state: RootState) => state.spatial
  );
  const catchmentDataset = catchmentSettingsTemp?.datasetConfig?.datasets;
  const { legendFilters } = useSelector((state: RootState) => state.map);

  const { showCatchmentPopup } = useSelector((state: RootState) => state.ui);
  const datasetFilters = useMemo(() => {
    return showCatchmentPopup
      ? catchmentDataset?.[datasetId ?? ""]?.filters || []
      : datasetFiltersDatex;
  }, [showCatchmentPopup, catchmentDataset, datasetId, datasetFiltersDatex]);

  const [datasetStreamId, setDatasetStreamId] = useState<string | undefined>();
  const [isLoadingDataset, setIsLoadingDataset] = useState(false);
  const [groupByValue, setGroupByValue] = useState<string | undefined>(
    dataset?.group_by
  );
  const { handleUpdateDatasetData, handleChangeSelectedCategoryDataset } =
    useDataset();
  const { clearLoadingState, showLoading, showError } = useLoadingAndToast();
  const { data: userProfile } = useGetUserProfileAPI();
  const { h3Configuration, setH3Configuration } = useH3Configuration({
    dataset,
    selectedDataset
  });
  const {
    isGettingAttributeValues,
    attributeValues,
    defaultSelectedValue,
    filterOptions,
    selectedOptions,
    savedFilters,
    showFilters,
    initialAdminArea,
    isAttributeFilterValid,
    selectAttributeItem,
    searchTerm,
    setShowFilters,
    handleAddAttributeFilter,
    getAvailableFilterOptions,
    handleSaveFilter,
    handleEditFilter,
    handleDeleteFilter,
    handleSearchChange,
    handleSelectFilter,
    handleAdminAreaChange,
    onFilterChange,
    selectedAdminArea,
    setSelectedAdminArea,
    adminAreaValid,
    isAdminAreaSelection,
    isAdminAreaSelectionValid,
    showErrorPOI,
    setShowErrorPOI,
    selectedAdminAreaIds,
    setSelectedAdminAreaIds,
    showAdmAreaTree,
    setShowAdmAreaTree,
    handleSaveAdminAreaTreeSelection,
    isLoadingAdminAreaHierarchy,
    resetAdminAreaIds,
    isOverrideAdminArea,
    setIsOverrideAdminArea,
    isFilterMode,
    isAdminTreeMode,
    isDetailMode,
    isUsingGlobalAdminAreaIds,
    groupByAttributeOptions,
    isGlobalFilterAdminAreaSelected,
    onSwitchCustomArea,
    shouldLinkToGlobalFilter
  } = useAttributeFilter({
    selectedDataset,
    initialDatasetFilters: datasetFilters,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    timezone: userProfile?.timezone as any,
    isAdminTreeEnabled,
    isGlobalFilterOverrideEnabled,
    dataset
  });

  const {
    mutateAsync: getDatasetDataPost,
    data: datasetData,
    isPending: isPendingGetDatasetData
  } = useGetDatasetDataMutation({
    timezone: userProfile?.timezone,
    options: {
      onMutate: () => {
        if (!showCatchmentPopup) {
          showLoading();
        }
      },
      onSuccess: () => {
        clearLoadingState();
      },
      onError: () => {
        showError();
      }
    }
  });

  useDatasetStream({
    datasetDetail: selectedDataset,
    showAnalysisResultPopup: showAnalysisResultPopup,
    datasetId: datasetStreamId,
    analysisId: showAnalysisResultPopup ? latestAnalysisId : undefined,
    enabled: !!datasetStreamId,
    timezone: userProfile?.timezone,
    filters: savedFilters,
    h3Configuration,
    group_by: groupByValue,
    onOpen: () => {
      showLoading();
      setIsLoadingDataset(true);
    },
    onStreamComplete: (data) => {
      const { datasetMetadata } = data;
      if (
        datasetMetadata?.spatial_aggregation_type ===
        EOutputAnalysisType.PROFILING
      ) {
        removeAllCatchmentMarkers();
      }
      const currentDatasetData = data.rawDataset;

      if (showCatchmentPopup) {
        dispatch(
          setDatasetConfigTemp({
            uniqueId: datasetId,
            rawDataset: currentDatasetData,
            filters: savedFilters
          })
        );
        const featureCollection =
          convertDatasetResponseToFeatureCollection(currentDatasetData);
        handleMovePositionToLayer(featureCollection);
      } else {
        // Regular mode - update dataset data and filters

        // If the dataset schema has changed, update the categories in the store
        if (
          JSON.stringify(datasetMetadata?.schema) !==
            JSON.stringify(dataset?.categories) &&
          datasetMetadata?.schema.length
        ) {
          dispatch(
            updateDatasetCategories({
              datasetId,
              categories: datasetMetadata?.schema
            })
          );
        }
        handleUpdateDatasetData(
          datasetId,
          currentDatasetData,
          h3Configuration,
          groupByValue
        );
        // update selected category if 'group_by' changes
        if (
          datasetMetadata.spatial_aggregation_type === "H3" &&
          datasetMetadata.schema.length > 0
        ) {
          const defaultCategory = getDefaultSelectedCategory(
            datasetMetadata.schema,
            datasetMetadata.spatial_aggregation_type
          );
          handleChangeSelectedCategoryDataset(
            { ...dataset, categories: datasetMetadata.schema },
            defaultCategory?.attribute_name || "",
            dataset.style.colorFamily,
            dataset.style.colorSteps
          );
        }
        dispatch(
          updateFilters({
            datasetId,
            filters: savedFilters
          })
        );
        dispatch(
          setLegendFilters({
            ...legendFilters,
            [datasetId]: []
          })
        );
      }
      // Close only if we're not just in showFilters mode
      onClose();
      setDatasetStreamId(undefined);
      setIsLoadingDataset(false);
      clearLoadingState();
    },
    onError: () => {
      setDatasetStreamId(undefined);
      setIsLoadingDataset(false);
      showError();
    }
  });

  const onSaveFilter = async () => {
    if (!selectedDataset) return;

    // First handle any needed filter saving
    let newFilter = savedFilters;

    if (showFilters) {
      newFilter = (await handleSaveFilter()) ?? savedFilters;
    } else if (showAdmAreaTree) {
      newFilter = handleSaveAdminAreaTreeSelection() ?? savedFilters;
      if (!isGlobalFilterOverrideEnabled) {
        setIsOverrideAdminArea(true);
      }
    }

    if (!showFilters && !showAdmAreaTree) {
      if (datasetData) {
        dispatch(
          setDatasetConfigTemp({
            uniqueId: datasetId,
            rawDataset: datasetData,
            filters: savedFilters
          })
        );
        onClose();
      } else {
        dispatch(
          toggleUseGlobalFilter({
            datasetId,
            useGlobalFilter: shouldLinkToGlobalFilter()
          })
        );
        setDatasetStreamId(selectedDataset.id);
      }
    }

    if (showCatchmentPopup && showFilters) {
      const currentDatasetData = await getDatasetDataPost({
        id: selectedDataset.id,
        filter: newFilter,
        h3Configuration
      });

      if (
        !isBVTDataForCatchmentEnabled &&
        (currentDatasetData?.rows?.length ?? 0) > MAX_POI
      ) {
        setShowErrorPOI(true);
      }
    }
  };

  const onDeleteFilter = async (filter: DatasetFilter) => {
    const newFilter = handleDeleteFilter(filter);
    if (selectedDataset && showCatchmentPopup) {
      await getDatasetDataPost({
        id: selectedDataset.id,
        filter: newFilter,
        h3Configuration
      });
    }
  };

  const isInvalidPOILength = useMemo(
    () =>
      !isBVTDataForCatchmentEnabled &&
      !!showCatchmentPopup &&
      ((datasetData?.rows?.length ?? 0) === 0 ||
        (datasetData?.rows?.length ?? 0) > MAX_POI),
    [
      isBVTDataForCatchmentEnabled,
      showCatchmentPopup,
      datasetData?.rows?.length
    ]
  );

  const showErrorPOICondition = useMemo(
    () =>
      showCatchmentPopup &&
      showErrorPOI &&
      selectedDataset &&
      isInvalidPOILength,
    [showCatchmentPopup, showErrorPOI, selectedDataset, isInvalidPOILength]
  );

  useEffect(() => {
    // validation when back
    if (showCatchmentPopup && !showFilters && datasetData) {
      setShowErrorPOI(isInvalidPOILength);
    }
  }, [
    showCatchmentPopup,
    showFilters,
    datasetData,
    isInvalidPOILength,
    setShowErrorPOI
  ]);

  const adminTreeConfig = {
    enabled: isAdminTreeEnabled,
    selectedIds: selectedAdminAreaIds,
    setSelectedIds: (ids: string[]) => {
      setSelectedAdminAreaIds(ids);
    }
  };

  const getTitleHeader = () => {
    if (showFilters) return "Edit Attribute Filter";
    if (showAdmAreaTree) return "Administrative Area";

    return "Attribute Filter";
  };

  const isSaveDisabled = () => {
    if (showFilters) return !isAttributeFilterValid;
    if (isAdminTreeMode)
      return (
        (isAdminAreaSelection && selectedAdminAreaIds.length === 0) ||
        isUsingGlobalAdminAreaIds
      );

    return !isAdminAreaSelectionValid;
  };

  return (
    <>
      <div className="flex flex-row items-start justify-between mb-4">
        <div>
          <div className="flex flex-row items-center gap-2">
            {(showFilters || showAdmAreaTree) && (
              <ChevLeftIcon
                data-testid="chev-left-icon"
                className={twMerge("w-4 h-4 cursor-pointer")}
                onClick={() => {
                  setShowFilters(false);
                  setShowAdmAreaTree(false);
                  if (isAdminTreeMode) resetAdminAreaIds();
                }}
              />
            )}
            <h2 className="text-txtHeading font-bold text-[20px]">
              {getTitleHeader()}
            </h2>
          </div>

          {!showFilters && !showAdmAreaTree && (
            <p className="text-txtBody text-[10px] mt-2">
              You may add filters to the attributes based on the dataset you
              select.
            </p>
          )}
        </div>

        <button
          className="p-0 bg-transparent border-0"
          onClick={() => {
            if (!isGlobalFilterOverrideEnabled) {
              setIsOverrideAdminArea(false);
            }
            onClose();
          }}
          data-testid="close-button"
        >
          <CloseIcon className="fill-dim-gray" />
        </button>
      </div>
      <div className="font-sans w-full h-full rounded-[12px] overflow-y-visible">
        <div className="flex flex-col min-h-0">
          {isFilterMode && (
            <AttributeFilter
              isEditMode
              savedFilters={savedFilters}
              getAvailableFilterOptions={getAvailableFilterOptions}
              handleSelectFilter={handleSelectFilter}
              selectAttributeItem={selectAttributeItem}
              defaultSelectedValue={defaultSelectedValue}
              attributeValues={attributeValues}
              selectedOptions={selectedOptions}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              isGettingAttributeValues={isGettingAttributeValues}
              onChange={onFilterChange}
            />
          )}

          {isGlobalFilterOverrideEnabled &&
            !showAnalysisResultPopup &&
            isAdminAreaSelection &&
            isDetailMode && (
              <CustomFilterArea
                disabled={!isGlobalFilterAdminAreaSelected}
                checked={isOverrideAdminArea}
                onSwitchChange={onSwitchCustomArea}
              />
            )}

          {isAdminTreeMode && (
            <AdminAreaSelection
              selectedData={selectedAdminArea || initialAdminArea}
              onSelectData={() => {}}
              options={{
                showCountryLevel: false,
                showTitle: false
              }}
              treeConfig={adminTreeConfig}
            />
          )}

          {isDetailMode && (
            <>
              {!showAnalysisResultPopup && (
                <LoadingWrapper
                  isLoading={isLoadingAdminAreaHierarchy}
                  loadingClassName="h-16"
                >
                  {isAdminTreeEnabled &&
                    isAdminAreaSelection &&
                    selectedAdminAreaIds.length > 0 && (
                      <SavedAdmArea
                        disabled={
                          isGlobalFilterAdminAreaSelected &&
                          !isOverrideAdminArea
                        }
                        filter={savedFilters.find(
                          (f) => f.attributeCode === "adm_area_level_1"
                        )}
                        onEdit={() => {
                          setShowAdmAreaTree(true);
                        }}
                        onDelete={() => {
                          setSelectedAdminAreaIds([]);
                        }}
                      />
                    )}
                </LoadingWrapper>
              )}

              {!isAdminTreeEnabled &&
                isAdminAreaSelection &&
                !showAnalysisResultPopup && (
                  <AdminAreaSelection
                    disabled={
                      isGlobalFilterAdminAreaSelected && !isOverrideAdminArea
                    }
                    selectedData={selectedAdminArea || initialAdminArea}
                    onSelectData={(newAdminArea) => {
                      setSelectedAdminArea(newAdminArea);
                      handleAdminAreaChange(newAdminArea);
                      if (!isGlobalFilterOverrideEnabled) {
                        setIsOverrideAdminArea(true);
                      }
                    }}
                    options={{
                      showCountryLevel: false,
                      showLabelAboveInput: true,
                      showLabel: false,
                      prefixLabel: "Select",
                      required: getAdminAreaRequired(selectedDataset),
                      visible: getAdminAreaVisibility(selectedDataset)
                    }}
                  />
                )}
              {!isAdminTreeEnabled && !showAnalysisResultPopup && (
                <AggregationFilter
                  value={h3Configuration}
                  spatialType={selectedDataset?.spatial_aggregation_type}
                  dataSource={selectedDataset?.source}
                  onChangeAggregationType={(value) => {
                    setH3Configuration((prev) => ({
                      ...prev,
                      aggregationType: value
                    }));
                  }}
                  onChangeGroupBy={(value) => {
                    setGroupByValue(value);
                  }}
                  openFrom="editPopup"
                  groupByValue={groupByValue}
                  groupByAttributeOptions={groupByAttributeOptions}
                />
              )}
              {showErrorPOICondition && (
                <CatchmentErrorMessage
                  datasetLength={datasetData?.rows?.length}
                />
              )}

              <div className="flex-1 flex flex-col">
                {isAdminTreeEnabled &&
                  isAdminAreaSelection &&
                  !isLoadingAdminAreaHierarchy &&
                  selectedAdminAreaIds.length === 0 &&
                  !showAnalysisResultPopup && (
                    <Button
                      variant="secondary"
                      className="h-[40px] w-full border-teal-500 bg-white text-teal-500 border text-sm font-bold disabled:border-gainsboro disabled:border disabled:text-gainsboro"
                      onClick={() => {
                        setShowAdmAreaTree(true);
                      }}
                      disabled={disabled}
                    >
                      Adm. Area
                    </Button>
                  )}
                {isAdminTreeEnabled && !showAnalysisResultPopup && (
                  <AggregationFilter
                    value={h3Configuration}
                    spatialType={selectedDataset?.spatial_aggregation_type}
                    dataSource={selectedDataset?.source}
                    onChangeAggregationType={(value) => {
                      setH3Configuration((prev) => ({
                        ...prev,
                        aggregationType: value
                      }));
                    }}
                    onChangeGroupBy={(value) => {
                      setGroupByValue(value);
                    }}
                    groupByValue={groupByValue}
                    openFrom="editPopup"
                    groupByAttributeOptions={groupByAttributeOptions}
                  />
                )}
                {!showAnalysisResultPopup && (
                  <Divider
                    className={twMerge(isAdminAreaSelection ? "my-3" : "mb-3")}
                  />
                )}
                {isAdminTreeEnabled && (
                  <Button
                    variant="secondary"
                    disabled={
                      !showAnalysisResultPopup &&
                      (disabled ||
                        savedFilters?.length === filterOptions.length ||
                        (selectedAdminAreaIds.length === 0 &&
                          selectedDataset?.source === "bvt"))
                    }
                    className={cn(
                      "h-[40px] w-full border border-teal-500 bg-white text-teal-500 text-sm font-bold disabled:border disabled:border-gainsboro disabled:text-gainsboro",
                      !showAnalysisResultPopup && "mb-3",
                      savedFilters.length > 0 && "mb-3"
                    )}
                    onClick={handleAddAttributeFilter}
                    data-testid={`${TEST_ID_PREFIX}-add-attribute-filter-button`}
                  >
                    Attribute Filter
                  </Button>
                )}

                {savedFilters?.length > 0 ? (
                  <SavedFiltersList
                    filterOptions={filterOptions}
                    filters={savedFilters}
                    onEdit={handleEditFilter}
                    onDelete={onDeleteFilter}
                  />
                ) : (
                  !isAdminAreaSelection &&
                  !showAnalysisResultPopup && <EmptyState />
                )}
              </div>
            </>
          )}
        </div>

        {!isAdminTreeEnabled && (
          <Button
            variant="secondary"
            disabled={
              disabled ||
              savedFilters?.length === filterOptions.length ||
              (showAnalysisResultPopup && !!latestAnalysisId
                ? false
                : !adminAreaValid)
            }
            className="h-[40px] mt-3 w-full border-teal-500 bg-white text-teal-500 border text-sm font-bold disabled:border-gainsboro disabled:border disabled:text-gainsboro"
            onClick={handleAddAttributeFilter}
            data-testid={`${TEST_ID_PREFIX}-add-attribute-filter-button`}
          >
            Add Attribute Filter
          </Button>
        )}

        <Button
          className="h-[40px] w-full btn-primary mt-4 text-sm !font-bold"
          disabled={
            !showAnalysisResultPopup &&
            (isSaveDisabled() ||
              showErrorPOI ||
              isPendingGetDatasetData ||
              isLoadingDataset)
          }
          onClick={onSaveFilter}
          data-testid={`${TEST_ID_PREFIX}-save-button`}
        >
          Save
        </Button>
      </div>
    </>
  );
};

export default EditPopupAttribute;
